import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RootState from '../redux/states/root.state';
import { AppDispatch } from '../redux/store';
import { resetLiveData, setLiveData } from '../redux/reducers/live.reducer';
import { resetSettingsData, setSettingsData } from '../redux/reducers/settings.reducer';
import { resetConstData, setConstData } from '../redux/reducers/const.reducer';
import { resetErrorData, setV1ErrorData } from '../redux/reducers/error.reducer';
import { v1ErrorsFromPayload } from '../services/mqtt/v1/v1Errors';
import { resetUpdateStatusData } from '../redux/reducers/updateStatus.reducer';
import { setStatusData } from '../redux/reducers/status.reducer';
import {
    setConnected,
    setConnecting,
    setConnectingFinished,
    setCurrentSpa,
    setDisconnected,
} from '../redux/reducers/spa.reducer';
import { fetchV1MqttToken } from '../services/api/v1Mqtt.api';
import V1SpaClient, { V1TokenInfo } from '../services/mqtt/v1/v1Client';
import { V1_ACTIVITY_CHANNELS, V1_CHANNEL_TRANSFORM, V1Channel, V1SlicePatch } from '../services/mqtt/v1/v1Transforms';
import V1Command from '../classes/v1Command.class';

/** No telemetry after the broker connected for this long: stop showing "Connecting…". */
const CONNECTING_TIMEOUT_MS = 12000;
/** Whatever happens (broker unreachable, token slow), stop showing "Connecting…" after this. */
const CONNECTING_HARD_TIMEOUT_MS = 25000;
/** A connected V1 spa that has published nothing for this long is shown offline. */
const SILENCE_OFFLINE_MS = 5 * 60 * 1000;
const WATCHDOG_TICK_MS = 5000;

/**
 * The V1 / Coldfire counterpart of `useMQTT`: one legacy-broker socket for the spa
 * being viewed, feeding the same `live` / `settings` / `const` redux slices and the
 * same connection flags on the `spa` slice, so every control page works unchanged.
 *
 * Pass an empty `spaId` to keep it idle (the caller decides V1 vs V2 — see
 * `useSpaCommand`). Returns the page's `Command`, or null until the socket exists.
 *
 * Unlike the Yocto's retained `/state` snapshot, V1 payloads arrive piecemeal and
 * Coldfire telemetry is partial, so the hook keeps a merged snapshot of each slice and
 * dispatches that whole object (the slice reducers replace, not merge).
 */
const useV1Mqtt = (spaId: string): V1Command | null => {
    const dispatch: AppDispatch = useDispatch();
    const accessToken = useSelector((state: RootState) => state.user.accessToken);
    const accessTokenRef = useRef<string | null>(accessToken);
    useEffect(() => {
        accessTokenRef.current = accessToken;
    }, [accessToken]);

    const [client, setClient] = useState<V1SpaClient | null>(null);

    // Running snapshots per slice; `V1Command` reads them to work out toggle targets.
    const liveRef = useRef<Record<string, any>>({});
    const settingsRef = useRef<Record<string, any>>({});
    const constRef = useRef<Record<string, any>>({});

    useEffect(() => {
        if (!spaId) return;

        const currentSpaId = spaId;
        let disposed = false;
        let connectingTimer: ReturnType<typeof setTimeout> | null = null;
        let hardTimer: ReturnType<typeof setTimeout> | null = null;
        let watchdog: ReturnType<typeof setInterval> | null = null;
        let lastActivityAt = 0;
        let shownConnected = false;

        // A fresh spa: nothing from the previous one may linger on the pages.
        liveRef.current = {};
        settingsRef.current = {};
        constRef.current = {};
        dispatch(resetLiveData());
        dispatch(resetSettingsData());
        dispatch(resetConstData());
        dispatch(resetErrorData());
        dispatch(resetUpdateStatusData());
        dispatch(setStatusData({}));
        dispatch(setCurrentSpa(currentSpaId));
        dispatch(setConnecting(currentSpaId));

        const finishConnecting = () => {
            if (connectingTimer) {
                clearTimeout(connectingTimer);
                connectingTimer = null;
            }
            dispatch(setConnectingFinished(currentSpaId));
        };

        const applyPatch = (patch: V1SlicePatch | null) => {
            if (!patch) return;
            if (patch.live) {
                liveRef.current = { ...liveRef.current, ...patch.live };
                dispatch(setLiveData(liveRef.current));
            }
            if (patch.settings) {
                settingsRef.current = { ...settingsRef.current, ...patch.settings };
                dispatch(setSettingsData(settingsRef.current));
            }
            if (patch.const) {
                constRef.current = { ...constRef.current, ...patch.const };
                dispatch(setConstData(constRef.current));
            }
        };

        const markOnline = () => {
            lastActivityAt = Date.now();
            if (!shownConnected) {
                shownConnected = true;
                dispatch(setConnected(currentSpaId));
            }
            if (connectingTimer) {
                clearTimeout(connectingTimer);
                connectingTimer = null;
            }
        };

        const getToken = async (): Promise<V1TokenInfo> => {
            const res = await fetchV1MqttToken(accessTokenRef.current, currentSpaId);
            return {
                token: res.access_token,
                expiresAt: Date.now() + (Number(res.expires_in) || 3600) * 1000,
                brokerUrl: res.broker_url ?? '',
                spaGuid: res.spa_guid,
            };
        };

        const v1 = new V1SpaClient(currentSpaId, {
            getToken,
            sourceName: 'Web',
            onMessage: (channel: V1Channel, payload: any) => {
                if (disposed) return;
                if (channel === 'errors') {
                    const errors = v1ErrorsFromPayload(payload);
                    if (errors) dispatch(setV1ErrorData(errors));
                    return;
                }
                const transform = V1_CHANNEL_TRANSFORM[channel];
                if (transform) {
                    applyPatch(transform(payload, {
                        live: liveRef.current,
                        settings: settingsRef.current,
                        const: constRef.current,
                    }));
                }
                if (V1_ACTIVITY_CHANNELS.has(channel)) markOnline();
            },
            onStatus: (status, detail) => {
                if (disposed) return;
                if (status === 'connected') {
                    // The broker is up; give the spa a moment to publish before calling it offline.
                    if (!shownConnected && !connectingTimer) {
                        connectingTimer = setTimeout(() => {
                            connectingTimer = null;
                            if (!shownConnected) {
                                console.warn('[useV1Mqtt] no telemetry from the spa after connecting to the broker');
                                dispatch(setConnectingFinished(currentSpaId));
                            }
                        }, CONNECTING_TIMEOUT_MS);
                    }
                } else if (status === 'error' && detail === 'token') {
                    // The backend would not mint a token (no legacy owner, auth service down):
                    // nothing to wait for, show the page offline.
                    finishConnecting();
                } else if (status === 'error' && detail === 'broker-url') {
                    finishConnecting();
                } else if (status === 'disconnected') {
                    // The socket dropped; the client retries with backoff. Keep whatever is on
                    // screen, the watchdog decides when silence means offline.
                    if (!shownConnected) finishConnecting();
                }
            },
        });

        // Silence watchdog: a V1 spa cannot be asked whether it is still there.
        watchdog = setInterval(() => {
            if (!shownConnected || !lastActivityAt) return;
            if (Date.now() - lastActivityAt >= SILENCE_OFFLINE_MS) {
                console.warn('[useV1Mqtt] spa silent for 5 minutes, showing it offline');
                shownConnected = false;
                dispatch(setDisconnected(currentSpaId));
            }
        }, WATCHDOG_TICK_MS);

        // The pages render nothing but a spinner while "connecting"; never leave them there.
        hardTimer = setTimeout(() => {
            hardTimer = null;
            if (!shownConnected) {
                console.warn('[useV1Mqtt] still not connected after the hard timeout, showing the page offline');
                finishConnecting();
            }
        }, CONNECTING_HARD_TIMEOUT_MS);

        setClient(v1);
        v1.start();

        return () => {
            disposed = true;
            if (connectingTimer) clearTimeout(connectingTimer);
            if (hardTimer) clearTimeout(hardTimer);
            if (watchdog) clearInterval(watchdog);
            v1.stop();
            setClient((prev) => (prev === v1 ? null : prev));
        };
    }, [spaId, dispatch]);

    return useMemo(() => {
        if (!client) return null;
        return new V1Command(
            (kind, body) => client.publish(kind, body),
            () => ({ live: liveRef.current, settings: settingsRef.current }),
        );
    }, [client]);
};

export default useV1Mqtt;
